import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Plus, Save, ShieldCheck, Trash2 } from 'lucide-react';
import { adminRoleService } from '../../shared/services';
import type { AdminRole } from '../../shared/types';

const PERMISSOES = [
  'system.manage', 'users.manage', 'entities.manage', 'entity.students.manage',
  'entity.trilhas.customize', 'entity.reports.view', 'students.progress.view',
  'courses.create', 'courses.edit', 'courses.delete', 'courses.manage', 'courses.view',
  'trilhas.create', 'trilhas.edit', 'trilhas.manage',
  'lessons.manage', 'lessons.view', 'videos.upload',
  'progress.update', 'certificates.view', 'certificates.revoke', 'reports.view',
];

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

type Formulario = { id?: string; name: string; description: string; permissions: string[] };

const vazio: Formulario = { name: '', description: '', permissions: [] };

export function AdminPapeisPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Formulario>(vazio);
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['admin-roles'],
    queryFn: adminRoleService.listar,
  });

  const salvar = useMutation({
    mutationFn: () =>
      form.id
        ? adminRoleService.atualizar(form.id, {
            name: form.name,
            description: form.description,
            permissions: form.permissions,
          })
        : adminRoleService.criar({
            name: form.name,
            description: form.description,
            permissions: form.permissions,
          }),
    onSuccess: () => {
      setAviso('Papel salvo.');
      setForm(vazio);
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível salvar o papel.')),
  });

  const remover = useMutation({
    mutationFn: (id: string) => adminRoleService.remover(id),
    onSuccess: () => {
      setAviso('Papel removido.');
      setForm(vazio);
      queryClient.invalidateQueries({ queryKey: ['admin-roles'] });
    },
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível remover o papel.')),
  });

  const editar = (role: AdminRole) => {
    setForm({ id: role.id, name: role.name, description: role.description ?? '', permissions: [...role.permissions] });
    setAviso(null);
  };

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <ShieldCheck size={30} /> Papéis e permissões
            </h1>
            <p className="text-gray-600 mt-1">Crie papéis e defina o que cada perfil pode acessar.</p>
          </div>
          <button
            type="button"
            onClick={() => setForm(vazio)}
            className="btn-secondary flex items-center gap-2"
          >
            <Plus size={16} /> Novo papel
          </button>
        </div>

        {aviso && <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">{aviso}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="card lg:col-span-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Papéis</h2>
            {isLoading ? (
              <div className="py-8 text-center text-gray-500">Carregando...</div>
            ) : (
              <div className="space-y-2">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => editar(role)}
                    className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                      form.id === role.id ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{role.name}</p>
                    <p className="text-xs text-gray-500">{role.permissions.length} permissão(ões)</p>
                  </button>
                ))}
              </div>
            )}
          </section>

          <form
            className="card lg:col-span-2"
            onSubmit={(event) => {
              event.preventDefault();
              setAviso(null);
              salvar.mutate();
            }}
          >
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {form.id ? `Editar ${form.name}` : 'Novo papel'}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Nome</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                  required
                />
              </label>
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Descrição</span>
                <input
                  value={form.description}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </label>
            </div>

            <h3 className="text-sm font-semibold text-gray-700 mt-5 mb-2">Permissões</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {PERMISSOES.map((permission) => (
                <label key={permission} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.permissions.includes(permission)}
                    onChange={(event) => {
                      const permissions = event.target.checked
                        ? [...form.permissions, permission]
                        : form.permissions.filter((item) => item !== permission);
                      setForm({ ...form, permissions });
                    }}
                  />
                  <code className="text-xs">{permission}</code>
                </label>
              ))}
            </div>

            <div className="flex items-center gap-2 mt-6">
              <button type="submit" disabled={salvar.isPending} className="btn-primary flex items-center gap-2 disabled:opacity-50">
                <Save size={16} /> {salvar.isPending ? 'Salvando...' : 'Salvar papel'}
              </button>
              {form.id && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Remover o papel ${form.name}?`)) remover.mutate(form.id!);
                  }}
                  className="btn-secondary text-red-600 flex items-center gap-2"
                >
                  <Trash2 size={16} /> Remover
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
